import { useCallback, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { getSecuritySummary, getSecurityFindings, getSecurityFinding, markRemediationCompleted } from '../lib/api-client';
import { FilterBar } from '../components/FilterBar';
import { deserialize } from '@shared/filter-serializer';
import type { FilterState, SecurityFinding, Severity, Remediation } from '@shared/types';
import type { ApiResponse, SecuritySummary, SecurityFindingDetail } from '../lib/api-client';
import { cn } from '../lib/utils';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { ShieldCheck, Loader2, AlertTriangle, ShieldX, ShieldAlert, X, ExternalLink, ChevronLeft, ChevronRight } from 'lucide-react';

const PAGE_SIZE = 25;

type SortField = 'severity' | 'resourceName' | 'exposureType' | 'projectOrAccount' | 'evaluatedAt';
type SortDir = 'asc' | 'desc';

interface FindingsState {
  findings: SecurityFinding[];
  total: number;
  page: number;
  totalPages: number;
  loading: boolean;
  error: string | null;
  stale: boolean;
}

export function SecurityDashboard() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [filters, setFilters] = useState<FilterState>(() => deserialize(searchParams));
  const [summary, setSummary] = useState<ApiResponse<SecuritySummary> | null>(null);
  const [summaryLoading, setSummaryLoading] = useState(true);
  const [summaryError, setSummaryError] = useState<string | null>(null);
  const [sortField, setSortField] = useState<SortField>('severity');
  const [sortDir, setSortDir] = useState<SortDir>('asc');
  const [selectedFindingId, setSelectedFindingId] = useState<string | null>(null);
  const [findingDetail, setFindingDetail] = useState<SecurityFindingDetail | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [state, setState] = useState<FindingsState>({
    findings: [],
    total: 0,
    page: 1,
    totalPages: 0,
    loading: true,
    error: null,
    stale: false,
  });

  // Fetch security summary
  useEffect(() => {
    async function fetchSummary() {
      setSummaryLoading(true);
      setSummaryError(null);
      try {
        const res = await getSecuritySummary();
        setSummary(res);
      } catch (err) {
        setSummaryError(
          err instanceof Error ? err.message : 'Failed to load security summary'
        );
      } finally {
        setSummaryLoading(false);
      }
    }
    fetchSummary();
  }, []);

  // Fetch findings with filters and pagination
  const fetchFindings = useCallback(
    async (page: number, currentFilters: FilterState) => {
      setState((prev) => ({ ...prev, loading: true, error: null }));
      try {
        const params: Record<string, string | number | undefined> = {
          page,
          pageSize: PAGE_SIZE,
        };

        if (currentFilters.severities.length > 0) {
          params.severity = currentFilters.severities[0];
        }
        if (currentFilters.providers.length > 0) {
          params.provider = currentFilters.providers.join(',');
        }
        if (currentFilters.resourceTypes.length > 0) {
          params.type = currentFilters.resourceTypes.join(',');
        }
        if (currentFilters.regions.length > 0) {
          params.region = currentFilters.regions.join(',');
        }
        if (currentFilters.projectsOrAccounts.length > 0) {
          params.project = currentFilters.projectsOrAccounts.join(',');
        }
        if (currentFilters.environments.length > 0) {
          params.environment = currentFilters.environments.join(',');
        }

        const response = await getSecurityFindings(params);
        const pageData = response.data;

        setState({
          findings: pageData?.data ?? [],
          total: pageData?.total ?? 0,
          page: pageData?.page ?? 1,
          totalPages: pageData?.totalPages ?? 0,
          loading: false,
          error: null,
          stale: response.stale,
        });
      } catch (err) {
        setState((prev) => ({
          ...prev,
          loading: false,
          error: err instanceof Error ? err.message : 'Failed to load findings',
        }));
      }
    },
    []
  );

  useEffect(() => {
    fetchFindings(1, filters);
  }, [filters, fetchFindings]);

  const handleFilterChange = useCallback((newFilters: FilterState) => {
    setFilters(newFilters);
  }, []);

  const handleClearAll = useCallback(() => {
    const emptyFilters: FilterState = {
      providers: [],
      resourceTypes: [],
      regions: [],
      projectsOrAccounts: [],
      environments: [],
      severities: [],
      search: '',
    };
    setFilters(emptyFilters);
    setSearchParams(new URLSearchParams(), { replace: true });
  }, [setSearchParams]);

  const handlePageChange = useCallback(
    (newPage: number) => {
      fetchFindings(newPage, filters);
    },
    [fetchFindings, filters]
  );

  const handleFindingClick = useCallback(
    async (finding: SecurityFinding) => {
      setSelectedFindingId(finding.id);
      setDetailLoading(true);
      try {
        const res = await getSecurityFinding(finding.id);
        setFindingDetail(res.data);
      } catch {
        // silently handle
      } finally {
        setDetailLoading(false);
      }
    },
    []
  );

  const handleCloseModal = useCallback(() => {
    setSelectedFindingId(null);
    setFindingDetail(null);
  }, []);

  const handleSort = useCallback((field: SortField) => {
    setSortField((prev) => {
      if (prev === field) {
        setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
        return prev;
      }
      setSortDir('asc');
      return field;
    });
  }, []);

  // Loading state for initial page load
  if (summaryLoading && state.loading) {
    return (
      <div className="flex items-center justify-center h-[60vh]">
        <div className="flex flex-col items-center gap-4">
          <div className="relative">
            <div className="h-12 w-12 rounded-full border-4 border-gray-100" />
            <Loader2 className="absolute inset-0 h-12 w-12 animate-spin text-primary-500" />
          </div>
          <p className="text-sm font-medium text-gray-400">Cargando datos de seguridad…</p>
        </div>
      </div>
    );
  }

  // Error state for summary
  if (summaryError && !summary) {
    return (
      <Card className="border-red-200/50 bg-gradient-to-r from-red-50 to-white">
        <CardContent className="flex items-center gap-4 p-6">
          <div className="flex h-10 w-10 items-center justify-center rounded-full bg-red-100">
            <AlertTriangle className="h-5 w-5 text-red-600" />
          </div>
          <p className="text-sm text-red-700">{summaryError}</p>
        </CardContent>
      </Card>
    );
  }

  const isStale = summary?.stale || state.stale;
  const summaryData = summary?.data;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div className="flex items-center gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-gradient-to-br from-rose-500 to-rose-600 shadow-md shadow-rose-500/20">
            <ShieldCheck className="h-5 w-5 text-white" />
          </div>
          <div>
            <h1 className="text-2xl font-bold tracking-tight text-gray-900">Seguridad</h1>
            <p className="text-sm text-gray-500">
              Postura de seguridad y hallazgos detectados en recursos de red.
            </p>
          </div>
        </div>
        {isStale && (
          <div className="flex items-center gap-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-1.5">
            <div className="h-2 w-2 rounded-full bg-amber-400 animate-pulse" />
            <span className="text-xs font-medium text-amber-700">Datos en caché</span>
          </div>
        )}
      </div>

      {/* Summary Cards */}
      {summaryData && (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-5">
          <SeverityCard title="Critical" count={summaryData.critical} severity="critical" icon={ShieldX} />
          <SeverityCard title="High" count={summaryData.high} severity="high" icon={ShieldAlert} />
          <SeverityCard title="Medium" count={summaryData.medium} severity="medium" icon={AlertTriangle} />
          <SeverityCard title="Low" count={summaryData.low} severity="low" icon={ShieldCheck} />
          <Card className="border-gray-100 bg-gradient-to-br from-white to-gray-50/50 transition-all hover:shadow-md hover:-translate-y-0.5">
            <CardContent className="p-5">
              <div className="flex items-center justify-between">
                <p className="text-xs font-medium uppercase tracking-wider text-gray-500">Total Evaluados</p>
              </div>
              <p className="mt-3 text-3xl font-bold tabular-nums text-gray-900">{summaryData.totalEvaluated.toLocaleString()}</p>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Filter bar */}
      <FilterBar
        onFilterChange={handleFilterChange}
        onClearAll={handleClearAll}
        showSeverity
        showSearch={false}
      />

      {/* Findings error */}
      {state.error && (
        <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800">
          {state.error}
        </div>
      )}

      {/* Findings loading */}
      {state.loading && (
        <div className="flex items-center justify-center py-12">
          <div className="h-8 w-8 animate-spin rounded-full border-4 border-gray-200 border-t-primary-600" />
        </div>
      )}

      {/* Empty state */}
      {!state.loading && !state.error && state.findings.length === 0 && (
        <EmptyState onClearFilters={handleClearAll} />
      )}

      {/* Findings table */}
      {!state.loading && !state.error && state.findings.length > 0 && (
        <>
          <div className="overflow-hidden rounded-lg border border-gray-200 bg-white shadow-sm">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <SortableHeader field="severity" label="Severity" current={sortField} dir={sortDir} onSort={handleSort} />
                  <SortableHeader field="resourceName" label="Recurso" current={sortField} dir={sortDir} onSort={handleSort} />
                  <SortableHeader field="exposureType" label="Problema" current={sortField} dir={sortDir} onSort={handleSort} />
                  <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                    Puertos / IPs
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                    Región
                  </th>
                  <SortableHeader field="projectOrAccount" label="Proyecto" current={sortField} dir={sortDir} onSort={handleSort} />
                  <SortableHeader field="evaluatedAt" label="Evaluado" current={sortField} dir={sortDir} onSort={handleSort} />
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {sortFindings(state.findings, sortField, sortDir).map((finding: any) => (
                  <tr
                    key={finding.id}
                    onClick={() => handleFindingClick(finding)}
                    className="cursor-pointer transition-colors hover:bg-gray-50"
                    role="link"
                    tabIndex={0}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        handleFindingClick(finding);
                      }
                    }}
                  >
                    <td className="whitespace-nowrap px-4 py-3 text-sm">
                      <SeverityBadge severity={finding.severity} />
                    </td>
                    <td className="px-4 py-3 text-sm">
                      <div className="font-medium text-gray-900 truncate max-w-[220px]">
                        {finding.resourceName || finding.resourceId.slice(0, 16)}
                      </div>
                      <div className="text-xs text-gray-500">
                        {finding.resourceType ? finding.resourceType.split('_').map((w: string) => w.charAt(0).toUpperCase() + w.slice(1)).join(' ') : ''}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-sm">
                      <div className="text-gray-900">
                        {finding.exposureType ? finding.exposureType.split('-').map((w: string) => w.charAt(0).toUpperCase() + w.slice(1)).join(' ') : finding.ruleId}
                      </div>
                      <div className="text-xs text-gray-500">
                        {finding.direction ?? '—'} · {finding.ruleId}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-sm">
                      {finding.ports.length > 0 ? (
                        <div className="flex flex-wrap gap-1">
                          {finding.ports.slice(0, 4).map((port: string) => (
                            <span key={port} className="inline-flex items-center rounded bg-red-50 px-1.5 py-0.5 text-xs font-mono text-red-700">
                              {port}
                            </span>
                          ))}
                          {finding.ports.length > 4 && (
                            <span className="text-xs text-gray-500">+{finding.ports.length - 4}</span>
                          )}
                        </div>
                      ) : (
                        <span className="text-xs text-gray-400">—</span>
                      )}
                      {finding.ipRanges.length > 0 && (
                        <div className="mt-1 flex flex-wrap gap-1">
                          {finding.ipRanges.slice(0, 2).map((ip: string) => (
                            <span key={ip} className="inline-flex items-center rounded bg-orange-50 px-1.5 py-0.5 text-xs font-mono text-orange-700">
                              {ip}
                            </span>
                          ))}
                          {finding.ipRanges.length > 2 && (
                            <span className="text-xs text-gray-500">+{finding.ipRanges.length - 2}</span>
                          )}
                        </div>
                      )}
                    </td>
                    <td className="whitespace-nowrap px-4 py-3 text-xs text-gray-600">
                      {finding.region || 'Global'}
                    </td>
                    <td className="whitespace-nowrap px-4 py-3 text-xs text-gray-600">
                      {finding.projectOrAccount || '—'}
                    </td>
                    <td className="whitespace-nowrap px-4 py-3 text-xs text-gray-500">
                      {new Date(finding.evaluatedAt).toLocaleDateString()}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          <Pagination
            page={state.page}
            totalPages={state.totalPages}
            total={state.total}
            onPageChange={handlePageChange}
          />
        </>
      )}

      {/* Finding Detail Modal */}
      {selectedFindingId && (
        <FindingModal
          finding={findingDetail}
          loading={detailLoading}
          onClose={handleCloseModal}
        />
      )}
    </div>
  );
}

/* ─── Sub-components ────────────────────────────────────────────────── */

function RemediationItem({
  remediation,
  findingId,
  onCompleted,
}: {
  remediation: Remediation;
  findingId: string;
  onCompleted: (remId: string) => void;
}) {
  const [completing, setCompleting] = useState(false);

  async function handleComplete() {
    setCompleting(true);
    try {
      await markRemediationCompleted(findingId, remediation.id);
      onCompleted(remediation.id);
    } catch {
      // silently handle
    } finally {
      setCompleting(false);
    }
  }

  return (
    <div className="rounded-lg border border-gray-100 bg-gray-50 p-4 space-y-2">
      <div className="flex items-center justify-between gap-2">
        <p className="text-sm font-medium text-gray-900">{remediation.description}</p>
        {remediation.status === 'completed' ? (
          <span className="shrink-0 rounded-full bg-green-100 px-2.5 py-0.5 text-xs font-medium text-green-800">
            ✓ Completada
          </span>
        ) : (
          <button
            onClick={handleComplete}
            disabled={completing}
            className="shrink-0 rounded-md bg-green-600 px-3 py-1.5 text-xs font-medium text-white shadow-sm hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {completing ? 'Marcando…' : 'Marcar completada'}
          </button>
        )}
      </div>
      <p className="text-xs text-gray-600">{remediation.riskDescription}</p>
      <ol className="list-decimal list-inside space-y-1 text-sm text-gray-700">
        {remediation.steps.map((step: string, i: number) => (
          <li key={i}>{step}</li>
        ))}
      </ol>
      {remediation.consoleUrl && (
        <a
          href={remediation.consoleUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-2 rounded-md border border-blue-200 bg-blue-50 px-3 py-1.5 text-xs font-medium text-blue-700 hover:bg-blue-100 transition-colors mt-1"
        >
          <svg className="h-3.5 w-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
          </svg>
          Abrir en consola
        </a>
      )}
    </div>
  );
}

function SortableHeader({
  field,
  label,
  current,
  dir,
  onSort,
}: {
  field: SortField;
  label: string;
  current: SortField;
  dir: SortDir;
  onSort: (f: SortField) => void;
}) {
  const isActive = current === field;
  return (
    <th
      className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 cursor-pointer select-none hover:text-gray-700 hover:bg-gray-100 transition-colors"
      onClick={() => onSort(field)}
      aria-sort={isActive ? (dir === 'asc' ? 'ascending' : 'descending') : 'none'}
    >
      <div className="flex items-center gap-1">
        {label}
        <span className={`inline-flex flex-col ${isActive ? 'text-primary-600' : 'text-gray-300'}`}>
          <svg className={`h-3 w-3 ${isActive && dir === 'asc' ? 'text-primary-600' : ''}`} viewBox="0 0 10 5" fill="currentColor">
            <path d="M5 0L10 5H0z" />
          </svg>
          <svg className={`h-3 w-3 ${isActive && dir === 'desc' ? 'text-primary-600' : ''}`} viewBox="0 0 10 5" fill="currentColor">
            <path d="M5 5L0 0h10z" />
          </svg>
        </span>
      </div>
    </th>
  );
}

function sortFindings(findings: any[], field: SortField, dir: SortDir): any[] {
  const severityOrder: Record<string, number> = { critical: 0, high: 1, medium: 2, low: 3 };
  return [...findings].sort((a, b) => {
    let cmp = 0;
    switch (field) {
      case 'severity':
        cmp = (severityOrder[a.severity] ?? 4) - (severityOrder[b.severity] ?? 4);
        break;
      case 'resourceName':
        cmp = (a.resourceName || a.resourceId).localeCompare(b.resourceName || b.resourceId);
        break;
      case 'exposureType':
        cmp = (a.exposureType || a.ruleId).localeCompare(b.exposureType || b.ruleId);
        break;
      case 'projectOrAccount':
        cmp = (a.projectOrAccount || '').localeCompare(b.projectOrAccount || '');
        break;
      case 'evaluatedAt':
        cmp = new Date(a.evaluatedAt).getTime() - new Date(b.evaluatedAt).getTime();
        break;
    }
    return dir === 'asc' ? cmp : -cmp;
  });
}

function FindingModal({
  finding,
  loading,
  onClose,
}: {
  finding: SecurityFindingDetail | null;
  loading: boolean;
  onClose: () => void;
}) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} />
      <div className="relative w-full max-w-3xl max-h-[85vh] overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-2xl animate-in fade-in-0 zoom-in-95">
        {/* Header */}
        <div className="flex items-center justify-between border-b border-gray-100 bg-white px-6 py-4">
          <div className="flex items-center gap-3">
            {!loading && finding && <SeverityBadge severity={finding.severity} />}
            <div>
              {finding ? (
                <>
                  <h2 className="text-lg font-bold text-gray-900">{finding.resourceName || finding.resourceId}</h2>
                  <p className="text-xs text-gray-500">
                    {finding.resourceType ? finding.resourceType.split('_').map((w: string) => w.charAt(0).toUpperCase() + w.slice(1)).join(' ') : ''}
                    {' · '}{finding.region || 'Global'}
                    {finding.projectOrAccount ? ` · ${finding.projectOrAccount}` : ''}
                  </p>
                </>
              ) : (
                <h2 className="text-lg font-bold text-gray-900">Finding Detail</h2>
              )}
            </div>
          </div>
          <button onClick={onClose} className="flex h-8 w-8 items-center justify-center rounded-lg text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-600">
            <X className="h-4 w-4" />
          </button>
        </div>

        {/* Scrollable Body */}
        <div className="overflow-y-auto max-h-[calc(85vh-73px)]">
          {loading && (
            <div className="flex items-center justify-center py-20">
              <Loader2 className="h-8 w-8 animate-spin text-primary-500" />
            </div>
          )}

        {!loading && finding && (
          <div className="p-6 space-y-6">
            {/* Problema */}
            <div className="rounded-xl border border-gray-100 bg-gray-50/50 p-5">
              <h3 className="text-sm font-semibold text-gray-900 mb-3">Problema detectado</h3>
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <span className="text-xs text-gray-500 uppercase">Tipo de exposición</span>
                  <p className="font-medium text-gray-900">
                    {finding.exposureType ? finding.exposureType.split('-').map((w: string) => w.charAt(0).toUpperCase() + w.slice(1)).join(' ') : '—'}
                  </p>
                </div>
                <div>
                  <span className="text-xs text-gray-500 uppercase">Dirección</span>
                  <p className="font-medium text-gray-900">{finding.direction || '—'}</p>
                </div>
                <div>
                  <span className="text-xs text-gray-500 uppercase">Puertos expuestos</span>
                  <div className="mt-1 flex flex-wrap gap-1">
                    {finding.ports && finding.ports.length > 0 ? finding.ports.map((p: string) => (
                      <span key={p} className="rounded bg-red-50 px-1.5 py-0.5 text-xs font-mono text-red-700">{p}</span>
                    )) : <span className="text-gray-400">—</span>}
                  </div>
                </div>
                <div>
                  <span className="text-xs text-gray-500 uppercase">IPs expuestas</span>
                  <div className="mt-1 flex flex-wrap gap-1">
                    {finding.ipRanges && finding.ipRanges.length > 0 ? finding.ipRanges.map((ip: string) => (
                      <span key={ip} className="rounded bg-orange-50 px-1.5 py-0.5 text-xs font-mono text-orange-700">{ip}</span>
                    )) : <span className="text-gray-400">—</span>}
                  </div>
                </div>
              </div>
              {finding.description && (
                <p className="mt-3 text-sm text-gray-700">{finding.description}</p>
              )}
            </div>

            {/* Console link */}
            {finding.consoleUrl && (
              <Button asChild variant="primary" size="sm" className="gap-2">
                <a href={finding.consoleUrl} target="_blank" rel="noopener noreferrer">
                  <ExternalLink className="h-3.5 w-3.5" />
                  Abrir recurso en consola
                </a>
              </Button>
            )}

            {/* Remediaciones */}
            <div>
              <h3 className="text-sm font-semibold text-gray-900 mb-3">Remediaciones</h3>
              {finding.remediations.length === 0 ? (
                <p className="text-sm text-gray-500 bg-gray-50 p-4 rounded-lg text-center">Sin remediaciones disponibles</p>
              ) : (
                <div className="space-y-3">
                  {finding.remediations.map((rem: Remediation) => (
                    <RemediationItem
                      key={rem.id}
                      remediation={rem}
                      findingId={finding.id}
                      onCompleted={(remId) => {
                        setFindingDetail((prev) => {
                          if (!prev) return prev;
                          return {
                            ...prev,
                            remediations: prev.remediations.map((r) =>
                              r.id === remId ? { ...r, status: 'completed' as const } : r
                            ),
                          };
                        });
                      }}
                    />
                  ))}
                </div>
              )}
            </div>

            {/* Meta */}
            <div className="border-t border-gray-100 pt-4 text-xs text-gray-500 flex items-center justify-between">
              <span>Regla: <code className="rounded bg-gray-100 px-1.5 py-0.5 font-mono">{finding.ruleId}</code></span>
              <span>Evaluado: {new Date(finding.evaluatedAt).toLocaleString()}</span>
            </div>
          </div>
        )}
        </div>
      </div>
    </div>
  );
}

function SeverityCard({
  title,
  count,
  severity,
  icon: Icon,
}: {
  title: string;
  count: number;
  severity: Severity;
  icon: React.ElementType;
}) {
  const styles: Record<Severity, { bg: string; icon: string; border: string; text: string }> = {
    critical: { bg: 'bg-gradient-to-br from-white to-red-50/30', icon: 'bg-red-100 text-red-600', border: 'border-red-100', text: 'text-red-700' },
    high: { bg: 'bg-gradient-to-br from-white to-orange-50/30', icon: 'bg-orange-100 text-orange-600', border: 'border-orange-100', text: 'text-orange-700' },
    medium: { bg: 'bg-gradient-to-br from-white to-yellow-50/30', icon: 'bg-yellow-100 text-yellow-600', border: 'border-yellow-100', text: 'text-yellow-700' },
    low: { bg: 'bg-gradient-to-br from-white to-green-50/30', icon: 'bg-green-100 text-green-600', border: 'border-green-100', text: 'text-green-700' },
  };
  const s = styles[severity];

  return (
    <Card className={cn('group transition-all duration-200 hover:shadow-md hover:-translate-y-0.5', s.bg, s.border)}>
      <CardContent className="p-5">
        <div className="flex items-center justify-between">
          <p className="text-xs font-medium uppercase tracking-wider text-gray-500">{title}</p>
          <div className={cn('rounded-lg p-2 transition-transform group-hover:scale-110', s.icon)}>
            <Icon className="h-4 w-4" />
          </div>
        </div>
        <p className={cn('mt-3 text-3xl font-bold tabular-nums', s.text)}>{count.toLocaleString()}</p>
      </CardContent>
    </Card>
  );
}

function SeverityBadge({ severity }: { severity: Severity }) {
  const styles: Record<Severity, string> = {
    critical: 'bg-red-100 text-severity-critical ring-red-200',
    high: 'bg-orange-100 text-severity-high ring-orange-200',
    medium: 'bg-yellow-100 text-severity-medium ring-yellow-200',
    low: 'bg-green-100 text-severity-low ring-green-200',
  };

  return (
    <span
      className={`inline-flex items-center rounded-md px-2 py-0.5 text-xs font-semibold ring-1 ring-inset ${styles[severity]}`}
    >
      {severity.charAt(0).toUpperCase() + severity.slice(1)}
    </span>
  );
}

function EmptyState({ onClearFilters }: { onClearFilters: () => void }) {
  return (
    <Card className="border-gray-100">
      <CardContent className="flex flex-col items-center justify-center px-6 py-16 text-center">
        <ShieldCheck className="h-12 w-12 text-gray-200" />
        <h3 className="mt-4 text-sm font-semibold text-gray-900">Sin hallazgos</h3>
        <p className="mt-1 text-sm text-gray-500">
          No se encontraron hallazgos con los filtros actuales.
        </p>
        <Button variant="primary" size="sm" onClick={onClearFilters} className="mt-4">
          Limpiar filtros
        </Button>
      </CardContent>
    </Card>
  );
}

function Pagination({
  page,
  totalPages,
  total,
  onPageChange,
}: {
  page: number;
  totalPages: number;
  total: number;
  onPageChange: (page: number) => void;
}) {
  return (
    <div className="flex items-center justify-between">
      <p className="text-sm text-gray-500">
        <span className="font-semibold text-gray-700">{total}</span> hallazgos total
      </p>
      <div className="flex items-center gap-2">
        <Button variant="outline" size="sm" onClick={() => onPageChange(page - 1)} disabled={page <= 1} className="gap-1">
          <ChevronLeft className="h-3.5 w-3.5" />
          Anterior
        </Button>
        <span className="text-xs font-medium text-gray-500">
          {page} / {totalPages}
        </span>
        <Button variant="outline" size="sm" onClick={() => onPageChange(page + 1)} disabled={page >= totalPages} className="gap-1">
          Siguiente
          <ChevronRight className="h-3.5 w-3.5" />
        </Button>
      </div>
    </div>
  );
}
